import { PropsWithChildren, type ReactNode } from 'react';

import { Dialog, DialogDismiss, DialogHeading } from '@ariakit/react';
import { motion } from 'motion/react';

import { colors } from '../../colors';
import { XCircleIcon } from '../icons';

import { u } from '../../styles/unit';
import { BoxProps } from '../Layout';
import { Stack } from '../Layout/Stack';
import * as css from './Modal.css';
type ModalVariant = 'full' | 'partial';

export type ModalBaseProps = {
    /** Controls whether the modal is visible. */
    open?: boolean;

    /** Called when the modal requests to close (backdrop click, ESC, etc.). */
    onDismiss: () => void;

    /** Visual style variant of the modal. */
    variant?: ModalVariant;

    /** Expands the modal beyond its default constrained width. */
    fullPartialWidth?: boolean;

    /** To limit navigation modals from jumping content make it fixed 90% of height */
    fullHeight?: boolean;

    /** Enables internal scrolling when content exceeds available height. */
    overflow?: boolean;
};

export type ModalFooterProps = {
    /** Primary action element, typically the main CTA button. */
    primary?: ReactNode;

    /** Secondary action element, typically a cancel or supporting action. */
    secondary?: ReactNode;
};

export type ModalProps = {
    /** Title displayed in the modal header. */
    title: string;

    /** Footer configuration or null to render without a footer. */
    footer?: ModalFooterProps | null;
} & ModalBaseProps;

export const ModalBase = ({
    children,
    open,
    onDismiss,
    variant,
    fullPartialWidth,
    fullHeight,
    overflow,
}: PropsWithChildren<ModalBaseProps>) => {
    return (
        <Dialog
            open={open}
            onClose={onDismiss}
            className={css.dialog}
            backdrop={
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={css.modalBackdrop}
                    onClick={onDismiss}
                />
            }
        >
            <div className={css.modal({ variant, fullPartialWidth, overflow, fullHeight })}>{children}</div>
        </Dialog>
    );
};

export const ModalHeader = ({ children, customButton }: PropsWithChildren<{ customButton?: ReactNode }>) => {
    return (
        <div className={css.header}>
            <DialogHeading className={css.title}>{children}</DialogHeading>
            {customButton ? (
                customButton
            ) : (
                <DialogDismiss className={css.dismiss}>
                    <XCircleIcon width={24} height={24} weight="fill" color={colors.blue[500]} />
                </DialogDismiss>
            )}
        </div>
    );
};

export type ModalBodyProps = Omit<BoxProps, 'className'> & {
    dimmed?: boolean;
};

export const ModalBody = ({ children, dimmed, ...rest }: ModalBodyProps) => {
    return (
        <Stack gap={u(4)} px={u(4)} flexShrink={0} flexGrow={0} {...rest} className={css.body({ dimmed })}>
            {children}
        </Stack>
    );
};

export const ModalFooter = ({ primary, secondary }: ModalFooterProps) => {
    return (
        <div className={css.footer}>
            <div className={css.footerPartition}>{secondary}</div>
            <div className={css.footerPartition}>{primary}</div>
        </div>
    );
};

/**
 * This is a feature complete component of to create a Modal.
 * Use it in default cases.
 *
 * However, if it's functionality does not match your intended design,
 * you can implement a custom modal using it's base components.
 *
 * Example:
 * ```
 * <ModalBase onDismiss={onDismiss} open={open} variant={variant}>
 *     <ModalHeader>{title}</ModalHeader>
 *     <ModalBody>{children}</ModalBody>
 *     <ModalFooter {...footer} />
 * </ModalBase>
 * ```
 */
export function Modal({
    title,
    children,
    footer,
    onDismiss,
    open,
    variant = 'full',
    fullPartialWidth,
}: PropsWithChildren<ModalProps>) {
    return (
        <ModalBase
            onDismiss={onDismiss}
            open={open}
            variant={variant}
            fullPartialWidth={variant === 'partial' && fullPartialWidth}
        >
            <ModalHeader>{title}</ModalHeader>
            <ModalBody>{children}</ModalBody>
            {(footer?.primary || footer?.secondary) && <ModalFooter {...footer} />}
        </ModalBase>
    );
}
